import { handleActions } from 'redux-actions';
import {
  requestFail,
  requestPending,
  setUsers,
  userByPnr,
  getDonorDetails,
  deleteDonorDetails,
  getDonorSettings,
  deleteDonorSettings,
  toggleDonorDetailsModal,
  toggleDonorSettingsModal,
  setLastChoosenDonor,
  setSearchPnr,
  setUsersPage,
  setUsersAmount,
  setDonationSites,
  normalizeDonorsSort,
  DONORS_DEFAULT_PAGE_SIZE,
} from './actions';
import { SET_CURRENT_BLOODBANK, LOGOUT_REQUEST } from '../auth/actionTypes';
import { DONOR_DONATION_SITE_CHANGED } from '../reservations/actionTypes';

export const initialUsersPaging = () => ({
  page: 1,
  pageSize: DONORS_DEFAULT_PAGE_SIZE,
  total: 0,
  totalPages: 0,
  sort: normalizeDonorsSort().key,
  order: normalizeDonorsSort().order,
  bloodbankId: null,
  scopeKey: null,
});

const num = (...candidates) => {
  const hit = candidates.find((v) => typeof v === 'number' && Number.isFinite(v));
  return hit === undefined ? null : hit;
};

export const initialState = {
  users: [],
  // the server total of the last donor query (page fetch or candidate count)
  usersAmount: 0,
  // the server page currently shown (1-based), its size, the totals for
  // the pager and the sort it was served in; bloodbankId + scopeKey
  // (donorsScopeKey of the filters) say which query it belongs to
  usersPaging: initialUsersPaging(),
  // CMT-57: GET /v1/bloodbanks `content` — the donation-site directory the
  // «Valgt sted» dropdown of «Giverdetaljer» is built from
  donationSites: [],
  userByPnr: null,
  donorDetailsData: null,
  donorSettingsData: null,
  isPending: false,
  isError: false,
  error: null,
  isToastErrorShown : false,
  donorDetailsModalIsOpen: false,
  donorSettingsModalIsOpen: false,
  lastChoosenDonor: null,
  searchPnr: "",
};

const usersReducer = handleActions({
  [requestFail]: (state, action) => ({
    ...state,
    isPending: false,
    isError: true,
    error: action.payload,
    isToastErrorShown: true,
  }),
  [requestPending]: (state) => ({
    ...state,
    isPending: true,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  // legacy (unpaged) call: every row; the total comes with the API's
  // paging block when present
  [setUsers]: (state, action) => {
    const content = Array.isArray(action.payload && action.payload.content) ? action.payload.content : [];
    const paging = (action.payload && action.payload.paging) || {};
    return {
      ...state,
      users: content,
      usersAmount: num(paging.total, content.length),
      isPending: false,
      isError: false,
      error: null,
      isToastErrorShown: false,
    };
  },
  // One server page REPLACES the list; the pager state is the server's
  // `paging` object, falling back to what the thunk requested when the
  // server omits a field (normalised like pushMessages messagesPaging).
  [setUsersPage]: (state, action) => {
    const { content, paging: serverPaging, requested, bloodbankId, scopeKey } = action.payload || {};
    const previous = state.usersPaging || initialUsersPaging();
    const asked = requested || {};
    const server = serverPaging || {};
    const pageSize = num(server.page_size, asked.pageSize, previous.pageSize, DONORS_DEFAULT_PAGE_SIZE);
    const total = num(server.total, 0);
    const totalPages = num(server.total_pages, pageSize > 0 ? Math.ceil(total / pageSize) : 0);
    const page = num(server.page, asked.page, 1);
    const sorting = normalizeDonorsSort(
      typeof server.sort === 'string' ? server.sort : asked.sort,
      typeof server.order === 'string' ? server.order : asked.order,
    );
    return {
      ...state,
      users: Array.isArray(content) ? content : [],
      usersAmount: total,
      usersPaging: {
        page,
        pageSize,
        total,
        totalPages,
        sort: sorting.key,
        order: sorting.order,
        bloodbankId: bloodbankId === undefined ? null : bloodbankId,
        scopeKey: scopeKey === undefined ? null : scopeKey,
      },
      isPending: false,
      isError: false,
      error: null,
      isToastErrorShown: false,
    };
  },
  [setUsersAmount]: (state, action) => ({
    ...state,
    usersAmount: num(action.payload && action.payload.total, 0),
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [userByPnr]: (state, action) => ({
    ...state,
    userByPnr: action.payload.data ? { ...action.payload.data, bloodTiter: action.payload.data.titer } : null, // for changing
    isPending: false,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [setDonationSites]: (state, action) => ({
    ...state,
    donationSites: Array.isArray(action.payload) ? action.payload : [],
  }),
  // CMT-57: the donor was moved to another bank — drop the local copies this
  // bank's screens render from. `users` is the server page on screen (the
  // needRefresh refetch reloads it, this just does not wait for it); the PNR
  // search row takes the server's new donationSiteIds, so DonorsList's
  // «donor of the current bank?» check hides it; `lastChoosenDonor` pointed
  // at the row that is gone — and it is the guard DonorsList's needRefresh
  // effect uses to refetch the OPEN details, which must not fire for the
  // moved donor (an unrequested PII read whose late result would land in
  // donorDetailsData after the modal closed). The pager totals are left to
  // the refetch.
  [DONOR_DONATION_SITE_CHANGED]: (state, action) => {
    const { donorId, moved, donationSiteIds } = action.payload || {};
    if (!moved || !donorId) return state;
    const searched = state.userByPnr;
    return {
      ...state,
      users: (state.users || []).filter((user) => !user || user.id !== donorId),
      userByPnr: searched && searched.id === donorId
        ? { ...searched, donationSiteIds: Array.isArray(donationSiteIds) ? donationSiteIds : [] }
        : searched,
      lastChoosenDonor: state.lastChoosenDonor === donorId ? null : state.lastChoosenDonor,
    };
  },
  [getDonorDetails]: (state, action) => ({
    ...state,
    donorDetailsData: {
      currentUser: {
        ...action.payload.res.data,
        bloodTiter: action.payload.res.data.titer // for changing
      },
      currentDetails: {
        ...action.payload.details, 
        user: {
          ...action.payload.res.data,
          bloodTiter: action.payload.res.data.titer // for changing
        }, 
      },
    },
    isPending: false,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [deleteDonorDetails]: (state) => ({
    ...state,
    donorDetailsData: null,
    isPending: false,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [getDonorSettings]: (state, action) => ({
    ...state,
    donorSettingsData: {
      ...action.payload.reservation, 
      user: {
        ...action.payload.res.data,
        bloodTiter: action.payload.res.data.titer // for changing
      }, 
    },
    isPending: false,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [deleteDonorSettings]: (state) => ({
    ...state,
    donorSettingsData: null,
    isPending: false,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [toggleDonorDetailsModal]: (state) => ({
    ...state,
    donorDetailsModalIsOpen: !state.donorDetailsModalIsOpen,
  }),
  [toggleDonorSettingsModal]: (state) => ({
    ...state,
    donorSettingsModalIsOpen: !state.donorSettingsModalIsOpen,
  }),
  [setLastChoosenDonor]: (state, action) => ({
    ...state,
    lastChoosenDonor: action.payload,
  }),
  [setSearchPnr]: (state, action) => ({
    ...state,
    searchPnr: action.payload || "",
  }),
  [SET_CURRENT_BLOODBANK]: (state) => ({
    ...state,
    searchPnr: "",
  }),
  [LOGOUT_REQUEST]: (state) => ({
    ...state,
    searchPnr: "",
  }),
},
initialState);

export default usersReducer;
